import { z } from 'zod';

export const PostStatusSchema = z.enum([
  'draft_topic',
  'topic_pending_client',
  'topic_approved',
  'topic_changes_requested',
  'topic_rejected',
  'content_generating',
  'content_review',
  'content_pending_client',
  'content_approved',
  'content_changes_requested',
  'content_rejected',
  'ready_to_publish',
  'scheduled',
  'published',
  'waiting_info',
]);
export type PostStatus = z.infer<typeof PostStatusSchema>;

/**
 * Coarse progress buckets — 15 post statuses collapsed into the 4 stages the
 * plan list's progress bar and slot counter show.
 *
 * Lives here, shared by API and web, on purpose: the API now computes these
 * counts in SQL (one GROUP BY for the whole org instead of a request per plan)
 * while the web app still renders the bar. If the two sides kept their own copy
 * of this mapping, "13/13 slot kész" could silently start meaning something
 * different from what the user saw yesterday.
 */
export const SLOT_BUCKETS = ['todo', 'progress', 'review', 'done'] as const;
export type SlotBucket = (typeof SLOT_BUCKETS)[number];

export const SLOT_BUCKET_BY_STATUS: Record<PostStatus, SlotBucket> = {
  draft_topic: 'todo',
  topic_pending_client: 'review',
  topic_approved: 'progress',
  content_generating: 'progress',
  content_review: 'progress',
  content_pending_client: 'review',
  content_approved: 'done',
  ready_to_publish: 'done',
  scheduled: 'done',
  published: 'done',
  topic_changes_requested: 'todo',
  content_changes_requested: 'todo',
  topic_rejected: 'todo',
  content_rejected: 'todo',
  waiting_info: 'todo',
};

/** The statuses belonging to one bucket — used to build the SQL FILTER lists. */
export function statusesInSlotBucket(bucket: SlotBucket): PostStatus[] {
  return (Object.keys(SLOT_BUCKET_BY_STATUS) as PostStatus[]).filter(
    (s) => SLOT_BUCKET_BY_STATUS[s] === bucket,
  );
}

/** Per-plan slot tallies, aggregated server-side. */
export interface SlotCounts extends Record<SlotBucket, number> {
  total: number;
}

export const BrandOrSalesSchema = z.enum(['brand', 'sales']);
export type BrandOrSales = z.infer<typeof BrandOrSalesSchema>;

export const PostActionSchema = z.enum([
  'submit_topic_for_client_review',
  'submit_content_for_client_review',
  'mark_content_approved',
  'mark_ready_to_publish',
  'mark_published',
  'archive',
  'manager_accept_revision_to_topic',
  'manager_accept_revision_to_content',
  'client_approve_topic',
  'client_request_changes_on_topic',
  'client_reject_topic',
  'client_approve_content',
  'client_request_changes_on_content',
  'client_reject_content',
  'enqueue_copy_generation',
  'complete_copy_generation',
  'fail_copy_generation',
]);
export type PostAction = z.infer<typeof PostActionSchema>;

export const PostSchema = z.object({
  id: z.string().guid(),
  monthlyPlanId: z.string().guid(),
  hotelId: z.string().guid(),
  scheduledFor: z.string().datetime(),
  /** True once deliberately scheduled to Meta; scheduledAt records when. */
  isScheduled: z.boolean(),
  scheduledAt: z.string().datetime().nullable(),
  /** When the post actually went live on Meta. Set when marked `published`. */
  publishedAt: z.string().datetime().nullable(),
  /** Soft-delete / archive marker. Non-null hides from active plan views. */
  archivedAt: z.string().datetime().nullable(),
  position: z.number().int(),
  status: PostStatusSchema,
  /** Where the post stood before a manager parked it in `waiting_info`; null
   *  otherwise. Drives the "vissza: …" shortcut in the status picker. */
  waitingInfoFrom: PostStatusSchema.nullable().optional(),
  brandOrSales: BrandOrSalesSchema,
  category: z.string().nullable(),
  /** Internal free-text "Paraméter neve"; staff-only, never in client views. */
  sourceParamName: z.string().nullable(),
  publishToFb: z.boolean(),
  publishToIg: z.boolean(),
  /** Currently selected topic title (post_topics.isSelected=true), or null. */
  selectedTopicTitle: z.string().nullable().optional(),
  /** Whether any topic suggestions exist for this post (useful for calendar UI). */
  hasTopicSuggestions: z.boolean().optional(),
  /** Whether the post has a current content row (any platform). Drives the
   *  phase-aware label of the legacy changes_requested decision (Téma-kör vs Tartalom-kör). */
  hasContent: z.boolean().optional(),
  /** When the client last requested changes on this post. Non-null drives the
   *  "Módosítást kért" badge — needed because a per-platform change request
   *  does not move the post out of `*_pending_client` on its own. */
  clientFeedbackAt: z.string().datetime().nullable().optional(),
  /** Per-platform client decisions from the latest content batch, present
   *  only while the post waits at the client AND the client has answered
   *  some platforms but not all. A post moves on only once every platform
   *  is decided, so without this the calendar said "ügyfélnél" about a post
   *  the client believed done (Reizner Nikolett, 2026-09-29). */
  clientDecisions: z
    .object({
      fb: z.enum(['approved', 'changes_requested', 'rejected']).nullable(),
      ig: z.enum(['approved', 'changes_requested', 'rejected']).nullable(),
      /** Platforms on the post (text or pictures) the client has not decided on
       *  yet. Empty = every platform is answered, with different verdicts. */
      pending: z.array(z.enum(['fb', 'ig'])),
    })
    .nullable()
    .optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Post = z.infer<typeof PostSchema>;
export type PostClientDecisions = NonNullable<Post['clientDecisions']>;

export const CreatePostSchema = z.object({
  scheduledFor: z.string().datetime(),
  brandOrSales: BrandOrSalesSchema.default('brand'),
  category: z.string().min(1).max(40).nullable().optional(),
  publishToFb: z.boolean().default(true),
  publishToIg: z.boolean().default(true),
});
export type CreatePost = z.infer<typeof CreatePostSchema>;

export const UpdatePostSchema = CreatePostSchema.partial().extend({
  /** Internal "Paraméter neve" — own normalisation (not the sourceUrl rules):
   *  trimmed, empty becomes null. */
  sourceParamName: z
    .string()
    .trim()
    .max(200)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  /** Toggle the "beidőzítve" flag. Sets scheduledAt server-side. */
  isScheduled: z.boolean().optional(),
});
export type UpdatePost = z.infer<typeof UpdatePostSchema>;

export const ApplyActionSchema = z.object({
  action: PostActionSchema,
  comment: z.string().max(4000).optional(),
});
export type ApplyAction = z.infer<typeof ApplyActionSchema>;

/**
 * Admin records a client decision that arrived offline (phone / email).
 * The decision drives the same state-machine transition the client would
 * have triggered on the public portal, but is attributed to the admin with
 * the verbatim client note + channel for the audit trail. Optionally fires
 * a confirmation email back to the client.
 */
export const OfflineDecisionSchema = z.object({
  decision: z.enum(['approve', 'request_changes', 'reject']),
  source: z.enum(['phone', 'email', 'other']),
  note: z.string().max(4000).optional(),
  notifyClient: z.boolean().default(false),
  /** Required when notifyClient is true — where to send the confirmation. */
  clientEmail: z.string().email().optional(),
});
export type OfflineDecision = z.infer<typeof OfflineDecisionSchema>;

/**
 * Manager override of a post's status — bypasses the strict state machine.
 * The manager can jump a post to ANY status EXCEPT the two `*_pending_client`
 * states (reaching "ügyfélnél" requires actually sending it via the dedicated
 * submit actions, which also dispatch the client email) and the transient
 * `content_generating` (a system-only state owned by the copy worker).
 *
 * When the target is an approval DECISION (topic_approved, content_approved,
 * the four phase-split changes_requested/rejected statuses) the manager may
 * attach a `note` + offline `source` (the client replied by phone/email) and optionally notify the
 * client by email — folding in what used to be the separate "offline
 * decision" panel. When the target is `published`, `publishedAt` is REQUIRED
 * (the exact moment the post went live on Meta).
 */
export const ManagerSettableStatusSchema = z.enum([
  'draft_topic',
  'topic_approved',
  'content_review',
  'content_approved',
  'ready_to_publish',
  'scheduled',
  'published',
  'topic_changes_requested',
  'content_changes_requested',
  'topic_rejected',
  'content_rejected',
  'waiting_info',
]);
export type ManagerSettableStatus = z.infer<typeof ManagerSettableStatusSchema>;

export const SetPostStatusSchema = z
  .object({
    status: ManagerSettableStatusSchema,
    /** Verbatim note — stored as an internal comment + in the audit log. */
    note: z.string().max(4000).optional(),
    /** How the client's decision arrived, when relaying one offline. */
    source: z.enum(['phone', 'email', 'other']).optional(),
    notifyClient: z.boolean().default(false),
    /** Required when notifyClient is true. */
    clientEmail: z.string().email().optional(),
    /** Required when status === 'published' — exact go-live moment. */
    publishedAt: z.string().datetime().optional(),
    /**
     * Required (true) to walk a `published` post back to an earlier state.
     * The explicit flag keeps an accidental picker click from un-publishing
     * the internal record of a post that is live on Meta.
     */
    confirmRepublish: z.boolean().optional(),
  })
  .refine((v) => v.status !== 'published' || !!v.publishedAt, {
    message: 'publishedAt kötelező, ha az állapot "published"',
    path: ['publishedAt'],
  })
  .refine((v) => !v.notifyClient || !!v.clientEmail, {
    message: 'clientEmail kötelező, ha notifyClient igaz',
    path: ['clientEmail'],
  });
export type SetPostStatus = z.infer<typeof SetPostStatusSchema>;

/**
 * Body of `POST /posts/:id/quick-status` — the one-click path: writes the
 * status conditionally (`expectedStatus`) and NOTHING else (no approval-bundle
 * closing, no client email, no AI job). `publishedAt` / `waitingInfoFrom`
 * restore the previous values and are only accepted on an `undo`.
 */
export const QuickSetStatusSchema = z
  .object({
    status: ManagerSettableStatusSchema,
    /** The status the caller saw; a mismatch is a 409 `status_changed`. */
    expectedStatus: PostStatusSchema,
    undo: z.boolean().optional(),
    publishedAt: z.string().datetime().nullable().optional(),
    waitingInfoFrom: PostStatusSchema.nullable().optional(),
  })
  .refine((v) => v.undo === true || v.publishedAt === undefined, {
    message: 'publishedAt csak visszavonáskor (undo) adható meg',
    path: ['publishedAt'],
  })
  .refine((v) => v.undo === true || v.waitingInfoFrom === undefined, {
    message: 'waitingInfoFrom csak visszavonáskor (undo) adható meg',
    path: ['waitingInfoFrom'],
  });
export type QuickSetStatus = z.infer<typeof QuickSetStatusSchema>;

/** Body of `POST /posts/bulk/quick-status`; one shared status and optional note. */
export const BulkQuickSetStatusSchema = z.object({
  postIds: z.array(z.string().guid()).min(1).max(200),
  status: ManagerSettableStatusSchema,
  note: z.string().trim().max(4000).optional(),
});
export type BulkQuickSetStatus = z.infer<typeof BulkQuickSetStatusSchema>;

/** The values an undo needs to put the post back exactly as it was. */
const QuickStatusPreviousSchema = z.object({
  postId: z.string().guid(),
  previousStatus: PostStatusSchema,
  previousPublishedAt: z.string().datetime().nullable(),
  previousWaitingInfoFrom: PostStatusSchema.nullable(),
});

/** Response of `POST /posts/:id/quick-status`: `previous*` plus the updated post (as `PATCH posts/:id/status`). */
export const QuickSetStatusResultSchema = QuickStatusPreviousSchema.extend({
  post: PostSchema.optional(),
});
export type QuickSetStatusResult = z.infer<typeof QuickSetStatusResultSchema>;

export const QuickStatusFailureCodeSchema = z.enum([
  'status_changed',
  'use_editor',
  'status_not_settable',
  'not_found',
]);
export type QuickStatusFailureCode = z.infer<typeof QuickStatusFailureCodeSchema>;

/** Response of `POST /posts/bulk/quick-status`: per-post outcome, one failure never stops the rest. */
export const BulkQuickSetStatusResultSchema = z.object({
  ok: z.array(QuickStatusPreviousSchema),
  failed: z.array(
    z.object({
      postId: z.string().guid(),
      code: QuickStatusFailureCodeSchema,
      title: z.string(),
    }),
  ),
});
export type BulkQuickSetStatusResult = z.infer<typeof BulkQuickSetStatusResultSchema>;

/**
 * Body of `POST /posts/:id/trigger-topic`. The optional instruction is the
 * manager's one-off steer for THIS generation round (tesztkör-4), mirroring
 * the copy regeneration's `tone`: short, appended to the prompt as data.
 */
export const TriggerPostTopicSchema = z
  .object({
    instruction: z.string().trim().min(1).max(200).optional(),
  })
  .default({});
export type TriggerPostTopic = z.infer<typeof TriggerPostTopicSchema>;

export const AutoGenerateSlotsSchema = z.object({
  count: z.number().int().min(1).max(60).optional(),
});
export type AutoGenerateSlots = z.infer<typeof AutoGenerateSlotsSchema>;

/** Move a post into a different plan (incl. from an archived/past plan). */
export const MovePostSchema = z.object({
  targetPlanId: z.string().guid(),
  newScheduledFor: z.string().datetime(),
});
export type MovePost = z.infer<typeof MovePostSchema>;

/** Filters for the org-wide content browser ("Tartalmak" menu). */
export const ContentQuerySchema = z.object({
  status: z
    .union([PostStatusSchema, z.array(PostStatusSchema)])
    .optional()
    .transform((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v])),
  hotelId: z
    .union([z.string().guid(), z.array(z.string().guid())])
    .optional()
    .transform((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v])),
  // Restrict to a single monthly plan (used by the "Jóváhagyott posztok" page,
  // where the plan dropdown is scoped to the selected hotel).
  monthlyPlanId: z.string().guid().optional(),
  // ISO calendar dates (YYYY-MM-DD); the content service appends the time
  // component itself. Validated so a malformed value can't reach the SQL.
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  q: z.string().max(120).optional(),
  sort: z.enum(['newest', 'oldest', 'hotel', 'status']).default('newest'),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ContentQuery = z.infer<typeof ContentQuerySchema>;

/** A row in the org-wide content browser — post DTO + display context. */
export const ContentRowSchema = z.object({
  id: z.string().guid(),
  hotelId: z.string().guid(),
  hotelName: z.string(),
  monthlyPlanId: z.string().guid(),
  year: z.number().int(),
  month: z.number().int(),
  scheduledFor: z.string().datetime(),
  status: PostStatusSchema,
  brandOrSales: BrandOrSalesSchema,
  title: z.string().nullable(),
  publishToFb: z.boolean(),
  publishToIg: z.boolean(),
  isScheduled: z.boolean(),
  /**
   * Current per-platform post text (post_contents where is_current=true).
   * Empty when the post has no generated/authored copy yet. Ordered fb→ig.
   * Powers the "Jóváhagyott posztok" copy-to-clipboard cards; the plain
   * content-browser table ignores it.
   */
  contents: z
    .array(
      z.object({
        platform: z.enum(['fb', 'ig']),
        body: z.string(),
      }),
    )
    .default([]),
});
export type ContentRow = z.infer<typeof ContentRowSchema>;

/**
 * One (hotel, status) group with its exact count.
 *
 * The dashboard's "Figyelmedet igényli" panel shows per-hotel counts ("4 poszt
 * vár belső reviewra"). Counting them from a fetched page of posts silently
 * understated them past the page limit, so the count comes from SQL instead —
 * a number the client cannot get wrong.
 */
export const ContentHotelStatusTallySchema = z.object({
  hotelId: z.string().guid(),
  status: PostStatusSchema,
  /** Groups with no rows are absent, so this is always at least 1. */
  count: z.number().int().positive(),
  /**
   * Earliest `scheduledFor` in the group. Doubles as the urgency key: the panel
   * orders by it ascending so the most imminent work is what survives the
   * five-item cut, not whatever happened to sort first.
   */
  soonestScheduledFor: z.string().datetime(),
});
export type ContentHotelStatusTally = z.infer<typeof ContentHotelStatusTallySchema>;

/**
 * Dashboard roll-up: the post tallies plus the next few scheduled posts, in one
 * response.
 *
 * Exists because the dashboard used to derive both by fetching
 * `/plans/:id/posts` once per plan — one request per monthly plan in the org,
 * every time the page was opened. The counts have to span *every* post, so
 * paging the content browser could not substitute for them.
 */
export const ContentSummarySchema = z.object({
  /** Non-archived posts across the org. */
  total: z.number().int().nonnegative(),
  /**
   * Post count per status. `partialRecord`, not `record`: statuses with no posts
   * are absent rather than zero, so the type has to admit missing keys — a full
   * `Record` would force every caller to trust an eleven-key object the SQL
   * GROUP BY never produces.
   */
  byStatus: z.partialRecord(PostStatusSchema, z.number().int().nonnegative()),
  /**
   * The same tallies broken down per hotel — `byStatus` is this, summed. Unbounded
   * by design: it is at most (hotels × statuses) rows of three scalars, which for
   * this tool is a couple of hundred at the very worst.
   */
  byHotelStatus: z.array(ContentHotelStatusTallySchema),
  /**
   * Soonest-first posts scheduled from `upcomingFrom` onwards (the API caps how
   * many it returns). Same row shape as the content browser.
   */
  upcoming: z.array(ContentRowSchema),
});
export type ContentSummary = z.infer<typeof ContentSummarySchema>;

export const ContentSummaryQuerySchema = z.object({
  /**
   * ISO instant; posts scheduled at or after it are "upcoming". The client sends
   * it so the cut-off follows the user's clock rather than the server's.
   */
  upcomingFrom: z.string().datetime().optional(),
  upcomingLimit: z.coerce.number().int().min(1).max(50).default(10),
});
export type ContentSummaryQuery = z.infer<typeof ContentSummaryQuerySchema>;

export const RotationWarningSchema = z.object({
  postId: z.string().guid(),
  prevPostId: z.string().guid(),
  category: z.string(),
  message: z.string(),
});
export type RotationWarning = z.infer<typeof RotationWarningSchema>;
